/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { setTenant, setPartner } from "@redux/slices/tenantSlice";
import { getPartners, getCustomers } from "./request/request";
import { useLeaveDetail } from "./useLeaveDetail";

interface Partner {
  id: number;
  nameDisplay: string;
  Domain: string;
  // Same concept as a customer's `customerId` — both are the tenant's
  // `chargebeeCustomerId`, which `/tenant/partners` now returns as `customerId`.
  customerId?: string | null;
}

interface Customer {
  id: number;
  nameDisplay: string;
  Domain: string;
  customerId?: string | null;
}

export type CustomerOption = { label: string; value: string };

/**
 * A partner the user is *browsing* in the selector but has not committed yet: its
 * customers are loaded so the Customer list can render them, while the app's tenant
 * context stays untouched. `customers: null` means that list is still in flight.
 */
type PreviewedPartner = { partner: Partner; customers: Customer[] | null };

/** Stable empty list, so a preview still loading doesn't rebuild `customerOptions` on every render. */
const NO_CUSTOMERS: Customer[] = [];

export interface UsePartnerCustomerSelectionArgs {
  tenantType: string; // "CUSTOMER" | "PARTNER" | "GLOBAL_ADMIN" | ...
  isLoading: boolean;
  isAuthenticated: boolean;
  getAccessTokenSilently: () => Promise<string>;
}

/**
 * Stable sentinel for the "Partner Account" option. The visible label still uses
 * `partnerName`, but selection compares against this constant `value`, avoiding the
 * race where two closures rebuild the label string from a stale `partnerName`.
 */
export const PARTNER_ACCOUNT_VALUE = "__partner_account__";

/**
 * All partner/customer selection data + behavior, extracted from
 * `PartnerCustomerDropdown` so the top-bar dropdown AND the new logo card can
 * share ONE source of truth (single fetch, single set of race guards / effects).
 * Behavior is unchanged from the original component.
 *
 * The hook is safe to call for any `tenantType` (including CUSTOMER): the load
 * effect and fetches are gated internally, so a CUSTOMER consumer that just
 * hides the UI incurs no requests.
 *
 * Two ways to switch partner, because the surfaces differ:
 *  - `selectPartnerById` — commits immediately (mobile sheet, top-bar dropdown).
 *  - `previewPartnerById` + `onSelectCustomer` — the desktop tenant card loads the
 *    partner's customers first and only commits once the customer (or "Partner
 *    Account") is picked, so the tenant context moves once per complete choice
 *    instead of twice per partner click.
 */
export function usePartnerCustomerSelection({
  tenantType,
  isLoading,
  isAuthenticated,
  getAccessTokenSilently,
}: UsePartnerCustomerSelectionArgs) {
  const dispatch = useAppDispatch();
  // A detail belongs to one customer: picking someone else returns to its list (CTX-1281).
  const leaveDetail = useLeaveDetail();
  const {
    partnerId,
    partnerName,
    tenantId,
    tenantName,
    partnerDomain,
    tenantDomain,
  } = useAppSelector((s) => s.selection);

  const [isFetching, setIsFetching] = useState(false);
  /**
   * Scoped to the partner fetch alone. `isFetching` also covers `loadCustomers`,
   * so reusing it for the Partner column would spin a list that is already loaded
   * every time a partner is picked. The two columns load independently and each
   * reports its own state.
   */
  const [isPartnerListLoading, setIsPartnerListLoading] = useState(false);
  const [partnersData, setPartnersData] = useState<Partner[]>([]);
  const [partnerOptions, setPartnerOptions] = useState<string[]>([]);
  const [customersData, setCustomersData] = useState<Customer[]>([]);
  const [previewedPartner, setPreviewedPartner] =
    useState<PreviewedPartner | null>(null);

  const selection = useAppSelector((s) => s.selection);
  const isPartnerView = tenantId === partnerId || tenantName === partnerName;
  const rightLabel = isPartnerView ? "Partner view" : "Customer view";
  const rightId =
    !isPartnerView && selection.customerId
      ? `ID: ${selection.customerId}`
      : undefined;

  const userProfile = useAppSelector((s) => s.userProfile);

  /**
   * Race guard: every `loadCustomers`/`fetchPartners` call increments this counter
   * and only the latest call is allowed to commit results. Prevents an in-flight stale
   * request (e.g. with `partnerId=0`) from overwriting fresh data.
   */
  const requestSeqRef = useRef(0);

  /**
   * Race guard for the previewed partner's customer loads. Kept separate from
   * `requestSeqRef` because the two are independent: a preview must never cancel
   * (or be cancelled by) a fetch that belongs to the committed selection.
   */
  const previewSeqRef = useRef(0);

  /**
   * For PARTNER, the effective partner id may not yet be in Redux when the load effect
   * runs (it's dispatched in a sibling effect). Fall back to `userProfile.tenantId` so
   * we never call `getCustomers(token, 0)` and never build the option list with an
   * empty `partnerName`.
   */
  const effectivePartnerId =
    partnerId || (tenantType === "PARTNER" ? userProfile.tenantId : 0);
  const effectivePartnerName =
    partnerName ||
    (tenantType === "PARTNER" ? userProfile.Tenant?.nameDisplay ?? "" : "");
  const effectivePartnerDomain =
    partnerDomain ||
    (tenantType === "PARTNER" ? userProfile.Tenant?.Domain ?? "" : "");

  /**
   * Hydrate the partner slice for PARTNER users when it hasn't been set yet.
   * Mirrors the previous bootstrap effect; keep separate from data fetching so
   * `loadCustomers` doesn't depend on this dispatch propagating first.
   */
  useEffect(() => {
    if (tenantType === "PARTNER" && partnerId === 0 && userProfile.tenantId) {
      dispatch(
        setPartner({
          id: userProfile.tenantId,
          name: userProfile.Tenant.nameDisplay,
          domain: userProfile.Tenant.Domain,
        })
      );
    }
  }, [
    tenantType,
    partnerId,
    userProfile.tenantId,
    userProfile.Tenant?.nameDisplay,
    userProfile.Tenant?.Domain,
    dispatch,
  ]);

  /**
   * Resilience: if a `tenantId` ends up set without a matching `tenantDomain`
   * (e.g. on rehydrate), fill it from `customersData`. Skip when we are in
   * Partner view (tenantId === partnerId) — that case is handled by the partner
   * selection itself, not by `customersData`.
   */
  useEffect(() => {
    if (!tenantId || tenantId === partnerId) return;
    if (tenantDomain) return;
    if (customersData.length === 0) return;
    const cust = customersData.find((c) => c.id === tenantId);
    if (!cust) return;
    dispatch(
      setTenant({
        id: cust.id,
        name: cust.nameDisplay,
        domain: cust.Domain,
        customerId: cust.customerId ?? null,
      })
    );
  }, [tenantId, partnerId, customersData, tenantDomain, dispatch]);

  // Load partners (GLOBAL_ADMIN) or customers (PARTNER) on mount / when auth changes
  useEffect(() => {
    if (isLoading || !isAuthenticated || tenantType === "CUSTOMER") return;

    if (tenantType === "GLOBAL_ADMIN") {
      fetchPartners();
      return;
    }

    // PARTNER — only fetch once we have an effective partner id
    if (effectivePartnerId) {
      loadCustomers(effectivePartnerId);
    }
  }, [tenantType, isLoading, isAuthenticated, effectivePartnerId]);

  const fetchPartners = async () => {
    const seq = ++requestSeqRef.current;
    setIsFetching(true);
    setIsPartnerListLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const resp = await getPartners(token, true);
      if (seq !== requestSeqRef.current) return;
      const raw = resp.data.data || [];
      setPartnersData(raw);
      // Display options sorted alphabetically (case-insensitive). Keep `raw`
      // untouched so the default-partner pick below still follows API order.
      const sortedPartnerNames = raw
        .map((p: any) => p.nameDisplay)
        .sort((a: string, b: string) =>
          a.localeCompare(b, undefined, { sensitivity: "base" })
        );
      setPartnerOptions(sortedPartnerNames);
      // The Partner column has everything it needs now; what follows only loads
      // the picked partner's customers, which the Customer column reports itself.
      setIsPartnerListLoading(false);
      // decide which partner to pick:
      const persisted = partnerId !== 0 && raw.find((p: any) => p.id === partnerId);
      if (persisted) {
        //skip dispatching setPartner so we don't reset tenantName
        await loadCustomers(partnerId);
      } else {
        // no persisted partner → fall back to first in the list
        const pick = raw[0];
        if (!pick) return;
        dispatch(
          setPartner({ id: pick.id, name: pick.nameDisplay, domain: pick.Domain })
        );
        await loadCustomers(pick.id);
      }
    } catch (err) {
      console.error("Error fetching partners", err);
    } finally {
      // Also clears the partner flag on the paths that never reached the success
      // branch above (a failed fetch), so the column falls back to "No options"
      // rather than spinning forever.
      if (seq === requestSeqRef.current) {
        setIsFetching(false);
        setIsPartnerListLoading(false);
      }
    }
  };

  const loadCustomers = async (pid: number) => {
    if (!pid) return;
    const seq = ++requestSeqRef.current;
    setIsFetching(true);
    try {
      const token = await getAccessTokenSilently();
      const resp = await getCustomers(token, pid);
      if (seq !== requestSeqRef.current) return;
      const raw = resp.data.data || [];
      setCustomersData(raw);

      /**
       * Default to first customer ONLY when there is no current selection at all.
       * If we are restoring a session where `tenantId === partnerId` (partner view),
       * keep partner view; don't auto-jump into a customer.
       */
      if (!tenantId && raw.length > 0) {
        const firstCust = raw[0];
        dispatch(
          setTenant({
            id: firstCust.id,
            name: firstCust.nameDisplay,
            domain: firstCust.Domain,
            customerId: firstCust.customerId ?? null,
          })
        );
        return;
      }

      // tenantId belongs to a *different* partner—ignore (caller will reset).
      if (tenantId && tenantId !== pid) {
        const persistedCust = raw.find((c: any) => c.id === tenantId);
        if (persistedCust) {
          dispatch(
            setTenant({
              id: persistedCust.id,
              name: persistedCust.nameDisplay,
              domain: persistedCust.Domain,
              customerId: persistedCust.customerId ?? null,
            })
          );
        }
      }
    } catch (err) {
      console.error("Error fetching customers", err);
    } finally {
      if (seq === requestSeqRef.current) setIsFetching(false);
    }
  };

  /**
   * The partner whose customers the selector is currently showing: the previewed
   * one while browsing, the committed one otherwise. A preview with `customers:
   * null` is still loading, so it contributes an empty list rather than falling
   * back to the previous partner's customers.
   */
  const shownCustomers = previewedPartner
    ? previewedPartner.customers ?? NO_CUSTOMERS
    : customersData;
  const shownPartnerName =
    previewedPartner?.partner.nameDisplay ?? effectivePartnerName;

  /**
   * Customer dropdown options, derived from `shownCustomers`. The "Partner Account"
   * row uses a stable sentinel value (`PARTNER_ACCOUNT_VALUE`) so click resolution
   * never depends on string-equality of a label rebuilt with a possibly stale
   * `partnerName`.
   */
  const customerOptions: CustomerOption[] = useMemo(() => {
    const partnerLabel = `${shownPartnerName || "Partner"} (Partner Account)`;
    // Customers sorted alphabetically (case-insensitive); "Partner Account"
    // stays pinned at the top regardless of ordering.
    const sortedCustomers = [...shownCustomers].sort((a, b) =>
      a.nameDisplay.localeCompare(b.nameDisplay, undefined, { sensitivity: "base" })
    );
    return [
      { label: partnerLabel, value: PARTNER_ACCOUNT_VALUE },
      ...sortedCustomers.map((c) => ({ label: c.nameDisplay, value: String(c.id) })),
    ];
  }, [shownPartnerName, shownCustomers]);

  /** Currently selected option value (sentinel for partner view, customer id otherwise). */
  const customerSelectedValue = useMemo(() => {
    // While previewing another partner none of the listed customers is the active
    // tenant yet, so nothing is highlighted — not even "Partner Account", which
    // would wrongly read as "this partner is already applied".
    if (previewedPartner) return "";
    if (!tenantId || tenantId === partnerId || tenantName === partnerName) {
      return PARTNER_ACCOUNT_VALUE;
    }
    return String(tenantId);
  }, [previewedPartner, tenantId, partnerId, tenantName, partnerName]);

  // User selects a partner from the dropdown
  const onSelectPartner = async (nameDisplay: string) => {
    const found = partnersData.find((p) => p.nameDisplay === nameDisplay);
    if (!found) return;
    if (found.id !== partnerId) leaveDetail();
    dispatch(
      setPartner({ id: found.id, name: found.nameDisplay, domain: found.Domain })
    );
    // loading customers for the new partner will reset tenant in Redux
    await loadCustomers(found.id);
  };

  /**
   * Partner options keyed by unique `id`. Partners can share a display name
   * (e.g. two distinct "Mith" tenants), so a name-keyed list would select and
   * highlight both. `value = String(id)` resolves selection to a single partner.
   * (The legacy `partnerOptions` string list is kept for the mobile
   * GenericDropdown, which is name-keyed and reworked in CTX-124.)
   */
  const partnerOptionsById: CustomerOption[] = useMemo(
    () =>
      [...partnersData]
        .sort((a, b) =>
          a.nameDisplay.localeCompare(b.nameDisplay, undefined, {
            sensitivity: "base",
          })
        )
        .map((p) => ({ label: p.nameDisplay, value: String(p.id) })),
    [partnersData]
  );

  /**
   * Partner highlighted in the id-keyed list: the previewed one while browsing, the
   * committed one otherwise.
   */
  const shownPartnerId = previewedPartner?.partner.id ?? partnerId;
  const partnerSelectedValue = shownPartnerId ? String(shownPartnerId) : "";

  /** True while a previewed partner's customer list is still loading. */
  const isCustomerListLoading =
    isFetching || (!!previewedPartner && previewedPartner.customers === null);

  // User selects a partner from the id-keyed list (tenant card, mobile sheet):
  // switches the tenant context immediately.
  const selectPartnerById = async (value: string) => {
    const found = partnersData.find((p) => String(p.id) === value);
    if (!found) return;
    if (found.id !== partnerId) leaveDetail();
    dispatch(
      setPartner({ id: found.id, name: found.nameDisplay, domain: found.Domain })
    );
    await loadCustomers(found.id);
  };

  /**
   * User picks a partner in the desktop dropdown: load its customers so the Customer
   * list can offer them, but leave the tenant context alone. Nothing is dispatched
   * here — the switch is committed by `onSelectCustomer`, once the choice is complete.
   * Picking the committed partner again just drops the preview.
   */
  const previewPartnerById = async (value: string) => {
    const found = partnersData.find((p) => String(p.id) === value);
    if (!found) return;

    const seq = ++previewSeqRef.current;
    if (found.id === partnerId) {
      setPreviewedPartner(null);
      return;
    }

    setPreviewedPartner({ partner: found, customers: null });
    try {
      const token = await getAccessTokenSilently();
      const resp = await getCustomers(token, found.id);
      if (seq !== previewSeqRef.current) return;
      setPreviewedPartner({ partner: found, customers: resp.data.data || [] });
    } catch (err) {
      console.error("Error fetching customers", err);
      // Stop the spinner and keep the preview usable: "Partner Account" still
      // commits the partner even when its customer list could not be loaded.
      if (seq === previewSeqRef.current) {
        setPreviewedPartner({ partner: found, customers: [] });
      }
    }
  };

  /**
   * Selector dismissed without completing the choice → drop the preview (and any
   * list still in flight) so the next open reflects the partner actually in use.
   */
  const cancelPartnerPreview = () => {
    previewSeqRef.current++;
    setPreviewedPartner(null);
  };

  /**
   * User selects a customer or the "Partner Account" sentinel. Compare by stable
   * value, never by the visible label.
   *
   * This is the selector's single commit point: a previewed partner is dispatched
   * here too, so the tenant context — and every refetch hanging off it — moves once,
   * and only on a complete partner+customer choice.
   */
  const onSelectCustomer = (value: string) => {
    const preview = previewedPartner;
    const customers = preview ? preview.customers ?? NO_CUSTOMERS : customersData;

    if (preview) {
      leaveDetail();
      // `setPartner` already lands the lens on the new partner's account view
      // (tenantId = partner id, customerId = null) — which is exactly what the
      // sentinel means, so that branch needs no further dispatch below.
      dispatch(
        setPartner({
          id: preview.partner.id,
          name: preview.partner.nameDisplay,
          domain: preview.partner.Domain,
        })
      );
      setCustomersData(customers);
      cancelPartnerPreview();
    }

    if (value === PARTNER_ACCOUNT_VALUE) {
      if (preview) return;
      if (effectivePartnerId !== tenantId) leaveDetail();
      dispatch(
        setTenant({
          id: effectivePartnerId,
          name: effectivePartnerName,
          domain: effectivePartnerDomain,
          customerId: null,
        })
      );
      return;
    }

    const numericId = Number(value);
    const found = customers.find((c) => c.id === numericId);
    if (!found) return;
    if (!preview && found.id !== tenantId) leaveDetail();
    dispatch(
      setTenant({
        id: found.id,
        name: found.nameDisplay,
        domain: found.Domain,
        customerId: found.customerId ?? null,
      })
    );
  };

  /**
   * Display value for the trigger: "X (Partner Account)" while in partner view,
   * otherwise the current tenant's name.
   */
  const customerDisplay = useMemo(() => {
    if (customerSelectedValue === PARTNER_ACCOUNT_VALUE) {
      return `${effectivePartnerName || "Partner"} (Partner Account)`;
    }
    return tenantName;
  }, [customerSelectedValue, effectivePartnerName, tenantName]);

  return {
    isFetching,
    // partner selector (name-keyed — mobile GenericDropdown)
    partnerOptions,
    partnerName,
    onSelectPartner,
    // partner selector (id-keyed — tenant card; handles duplicate names)
    isPartnerListLoading,
    partnerOptionsById,
    partnerSelectedValue,
    selectPartnerById,
    // deferred partner switch (desktop tenant-card dropdown): preview → commit on
    // customer pick, or discard when the dropdown closes unresolved
    previewPartnerById,
    cancelPartnerPreview,
    // customer selector
    isCustomerListLoading,
    customerOptions,
    customerDisplay,
    customerSelectedValue,
    onSelectCustomer,
    // view meta
    isPartnerView,
    rightLabel,
    rightId,
    customerId: selection.customerId,
    // Partner-side identifier for the tenant card. Same concept as a customer's
    // `customerId` (both come from the tenant's `chargebeeCustomerId`); the
    // partners endpoint returns it as `customerId`. Only populated for
    // GLOBAL_ADMIN (who fetch partners).
    partnerCode: partnersData.find((p) => p.id === partnerId)?.customerId ?? null,
  };
}
